import { omit, pick } from 'lodash-es'
import moment from 'moment'
import { Entries, Entry } from 'type-fest'
import { z } from 'zod'

import { downloadFileByLink } from '@/lib/downloadFileByLink'
import { replaceEntity } from '@/lib/entity/replaceEntity'
import { captureAndLogError } from '@/lib/error'
import { isNil } from '@/lib/isNil'
import { IAction } from '@/redux/actions/action-helpers'
import { ExpensesTypes } from '@/redux/actions/actions.types'
import { ApplicationState } from '@/redux/stores/types'
import {
	DailyAllowanceHandler,
	ExpenseHandler,
	MileageHandler,
	rawVatRateNone,
	StandardExpenseHandler,
} from '@/travelsuit/expenses'
import { CountryName } from '@/types/common'
import {
	DailyAllowanceModel,
	DailyAllowanceModelZ,
	DailyAllowanceRequest,
	DailyAllowanceRequestZ,
	DailyAllowanceResponse,
} from '@/types/expenses/dailyAllowance'
import { ExpenseModel, ExpenseRequest, ExpenseType, MerchantCountry } from '@/types/expenses/expense'
import { MileageModel, MileageModelZ, MileageRequest, MileageRequestZ, MileageResponse } from '@/types/expenses/mileage'
import {
	ExpenseOcrSuggestionsLocal,
	ExpenseOcrSuggestionsResponse,
	StandardExpenseModel,
	StandardExpenseModelZ,
	StandardExpenseRequest,
	StandardExpenseRequestZ,
	StandardExpenseResponse,
} from '@/types/expenses/standardExpense'
import { ExpenseModelUnion, ExpenseResponseUnion } from '@/types/expenses/unions'
import { CountriesCode } from '@/types/locale'

function expensesReducer(state: ApplicationState['expenses'] = [], action: IAction) {
	switch (action.type) {
		case ExpensesTypes.GetExpense.SUCCESS: {
			const normalized = normalize(action.payload)
			return normalized ? replaceEntity(state, normalized.data.id, normalized, (entity) => entity.data.id) : state
		}
		case ExpensesTypes.GetExpenses.SUCCESS:
			return action.payload.map(normalize)
		case ExpensesTypes.ExportExpenses.SUCCESS: {
			downloadFileByLink(
				window.URL.createObjectURL(action.payload),
				`expenses-${new Date().toISOString().slice(0, 10)}.${action.requestPayload.fileType}`,
			)

			return state
		}
		default:
			return state
	}
}

export default expensesReducer

export function normalize(expense: ExpenseResponseUnion) {
	const mapTypeToNormalized: Record<ExpenseType, (expense: ExpenseResponseUnion) => ExpenseHandler> = {
		standard: normalizeStandardExpense,
		mileage: (expense) => normalizeMileage(expense as MileageResponse),
		daily_allowance: normalizeDailyAllowance,
	}

	return mapTypeToNormalized[expense.expense_type](expense)
}

export function denormalize(expense: ExpenseModelUnion) {
	const mapTypeToDenormalized: Record<typeof expense.type, (expense: ExpenseModelUnion) => ExpenseRequest> = {
		standard: denormalizeStandardExpense,
		mileage: denormalizeMileage,
		daily_allowance: denormalizeDailyAllowance,
	}

	return mapTypeToDenormalized[expense.type](expense)
}

export function normalizeStandardExpense(se: StandardExpenseResponse) {
	const parsedSe = StandardExpenseModelZ.safeParse({
		...pick(se, ['id', 'attendees', 'category', 'currency', 'description', 'merchant', 'reimbursable', 'submitter']),
		amount: se.amount?.toString() ?? '',
		createdAt: new Date(se.created_dt),
		date: se.date && new Date(se.date),
		file: se.file_data,
		merchantCountry:
			se.merchant_country_name && se.merchant_country_code
				? {
						code: se.merchant_country_code,
						name: se.merchant_country_name,
					}
				: { code: null, name: '' },
		...('expense_report' in se && se.expense_report && se.report_id
			? {
					report: { id: se.report_id, name: se.expense_report, amount: se.report_amount },
				}
			: {}),
		ocr: {
			suggestions: se.ocr_results
				? Object.fromEntries(
						(Object.entries(se.ocr_results) as Entries<ExpenseOcrSuggestionsResponse>)
							.map(normalizeOcrSuggestion)
							.filter(Boolean) as Entries<ExpenseOcrSuggestionsLocal>,
					)
				: {},
			status: se.ocr_status,
		},
		status: se.expense_status,
		type: se.expense_type,
		vatRate:
			'vat_rate' in se && se.vat_rate
				? se.vat_rate
				: se.merchant_country_name && se.merchant_country_code
					? rawVatRateNone
					: undefined,
	})

	if (!parsedSe.success) {
		captureAndLogError(parsedSe.error, { level: 'error', extra: { source: 'response_validation' } })
		console.error(z.prettifyError(parsedSe.error), se.id)
	}

	return new StandardExpenseHandler(parsedSe.data!)
}

function denormalizeStandardExpense(se: StandardExpenseModel): StandardExpenseRequest {
	const parsedSe = StandardExpenseRequestZ.safeParse({
		...pick(se, ['currency', 'merchant']),
		...denormalizeExpense(se),
		amount: Number(se.amount),
		attendees: se.attendees.map((attendee) =>
			attendee.id === 0 ? omit(attendee, ['id', 'image_url']) : { id: attendee.id },
		),
		category: se.category ?? null,
		date: moment(se.date).format('YYYY-MM-DD'),
		file_data: se.file ?? null,
		merchant_country_name: se.merchantCountry.name,
		ocr_results: Object.fromEntries(
			(Object.entries(se.ocr.suggestions) as Entries<typeof se.ocr.suggestions>).flatMap(denormalizeOcrSuggestion),
		),
		ocr_status: se.ocr.status,
		vat_rate_id: se.vatRate!.id === -1 ? null : se.vatRate!.id,
	})

	if (!parsedSe.success) {
		captureAndLogError(parsedSe.error, { level: 'error', extra: { source: 'request_validation' } })
		console.error(z.prettifyError(parsedSe.error), se.id)
	}

	return parsedSe.data!
}

function normalizeMileage(mileage: MileageResponse) {
	const parsedMileage = MileageModelZ.safeParse({
		...pick(mileage, ['id', 'amount', 'attendees', 'currency', 'description', 'reimbursable', 'submitter']),
		createdAt: new Date(mileage.created_dt),
		date: new Date(mileage.date),
		distance: {
			calculatedValue: mileage.calculated_distance,
			inputValue: mileage.distance.toString(),
			showSuggestion: mileage.show_calculated_distance,
		},
		file: mileage.file_data,
		isTravelRelated: isNil(mileage.is_travel_related)
			? mileage.is_travel_related
			: (Number(mileage.is_travel_related).toString() as '0' | '1'),
		points: mileage.destination_points.map((point) => ({ action: { id: point.id }, full_address: point.full_address })),
		rate: mileage.mileage_rate,
		status: mileage.expense_status,
		type: mileage.expense_type,
		unitOfLength: mileage.unit_of_length.toUpperCase(),
		...('expense_report' in mileage && mileage.expense_report && mileage.report_id
			? {
					report: {
						id: mileage.report_id,
						name: mileage.expense_report,
						amount: mileage.report_amount,
					},
				}
			: {}),
	})

	if (!parsedMileage.success) {
		captureAndLogError(parsedMileage.error, { level: 'error', extra: { source: 'response_validation' } })
		console.error(z.prettifyError(parsedMileage.error), mileage.id)
	}

	return new MileageHandler(parsedMileage.data!)
}

function denormalizeMileage(mileage: MileageModel): MileageRequest {
	const parsedMileage = MileageRequestZ.safeParse({
		...denormalizeExpense(mileage),
		attendees: mileage.attendees.map((attendee) =>
			attendee.id === 0 ? omit(attendee, ['id', 'image_url']) : { id: attendee.id },
		),
		calculated_distance: mileage.distance.calculatedValue,
		currency: mileage.rate.currency,
		date: moment(mileage.date).format('YYYY-MM-DD'),
		destination_points: mileage.points.map((item) => ({ id: item.action.id, full_address: item.full_address })),
		distance: Number(mileage.distance.inputValue),
		file_data: mileage.file,
		is_travel_related: Boolean(Number(mileage.isTravelRelated)),
		mileage_rate_id: mileage.rate.id,
		show_calculated_distance: mileage.distance.showSuggestion,
		unit_of_length: mileage.unitOfLength.toLowerCase(),
	})

	if (!parsedMileage.success) {
		captureAndLogError(parsedMileage.error, { level: 'error', extra: { source: 'request_validation' } })
		console.error(z.prettifyError(parsedMileage.error), mileage.id)
	}

	return parsedMileage.data!
}

export function normalizeDailyAllowance(da: DailyAllowanceResponse) {
	const parsedDa = DailyAllowanceModelZ.safeParse({
		...pick(da, ['id', 'amount', 'category', 'currency', 'description', 'reimbursable', 'submitter']),
		destination: { name: da.destination, id: da.destination_id },
		rates: da.daily_allowance_rates.map((rate) => ({ ...rate, number_of_days: rate.number_of_days.toString() })),
		startDate: new Date(da.start_date),
		endDate: new Date(da.end_date),
		startTime: da.start_time,
		endTime: da.end_time,
		type: da.expense_type,
		status: da.expense_status,
		file: da.file_data,
		isTravelRelated: isNil(da.is_travel_related)
			? da.is_travel_related
			: (Number(da.is_travel_related).toString() as '0' | '1'),
		createdAt: new Date(da.created_dt),
		...('expense_report' in da && da.expense_report && da.report_id
			? {
					report: {
						id: da.report_id,
						name: da.expense_report,
						amount: da.report_amount,
					},
				}
			: {}),
	})

	if (!parsedDa.success) {
		captureAndLogError(parsedDa.error, { level: 'error', extra: { source: 'response_validation' } })
		console.error(z.prettifyError(parsedDa.error), da.id)
	}

	return new DailyAllowanceHandler(parsedDa.data!)
}

export function denormalizeDailyAllowance(da: DailyAllowanceModel): DailyAllowanceRequest {
	const parsedDa = DailyAllowanceRequestZ.safeParse({
		...pick(da, ['category']),
		...denormalizeExpense(da),
		file_data: da.file ?? null,
		destination_id: da.destination.id,
		is_travel_related: Boolean(Number(da.isTravelRelated)),
		daily_allowance_rates: da.rates.map((rate) => {
			const number_of_days = Number(rate.number_of_days)
			return {
				number_of_days,
				amount: parseFloat((number_of_days * rate.daily_allowance_rate.amount).toFixed(2)),
				rate_id: rate.daily_allowance_rate.id,
			}
		}),
		amount: parseFloat(
			da.rates
				.reduce(
					(sum, rate) => sum + parseFloat((Number(rate.number_of_days) * rate.daily_allowance_rate.amount).toFixed(2)),
					0,
				)
				.toFixed(2),
		),
		start_date: moment(da.startDate).format('YYYY-MM-DD'),
		end_date: moment(da.endDate).format('YYYY-MM-DD'),
		start_time: da.startTime,
		end_time: da.endTime,
	})

	if (!parsedDa.success) {
		captureAndLogError(parsedDa.error, { level: 'error', extra: { source: 'request_validation' } })
		console.error(z.prettifyError(parsedDa.error), da.id)
	}

	return parsedDa.data!
}

export function normalizeOcrSuggestion<T extends ExpenseOcrSuggestionsResponse>(
	ocrSuggestion: Entry<T>,
	index: number,
	ocrSuggestions: Entries<T>,
): Entry<ExpenseOcrSuggestionsLocal> | null {
	const [key, value] = ocrSuggestion

	switch (key) {
		case 'amount':
			return [key, value?.toString() ?? '']
		case 'date':
			return [key, new Date(value!)]
		case 'merchant_country_code':
			return [
				'merchantCountry',
				{
					code: value as CountriesCode,
					name: ocrSuggestions.find(([key]) => key === 'merchant_country_name')![1] as CountryName,
				},
			]
		case 'merchant_country_name':
			return null
		default:
			return [key, value] as Entry<ExpenseOcrSuggestionsLocal>
	}
}

function denormalizeOcrSuggestion(
	ocrSuggestion: Entry<StandardExpenseModel['ocr']['suggestions']>,
): Entry<ExpenseOcrSuggestionsResponse>[] {
	const [key, value] = ocrSuggestion

	switch (key) {
		case 'amount':
			return [[key, Number(value)]]
		case 'date':
			return [[key, moment(value as Date).format('YYYY-MM-DD')]]
		case 'merchantCountry': {
			const { code, name } = value as MerchantCountry
			return [
				['merchant_country_code', code],
				['merchant_country_name', name],
			]
		}
		default:
			return [[key, value] as Entry<ExpenseOcrSuggestionsResponse>]
	}
}

function denormalizeExpense(expense: ExpenseModel) {
	const reportId = Number(expense.report?.id)
	return {
		...pick(expense, ['description', 'reimbursable']),
		report_id: Number.isNaN(reportId) ? undefined : reportId,
		expense_report: expense.report?.name || '',
		expense_status: expense.status,
		expense_type: expense.type,
		submitter_id: expense.submitter.id,
	}
}
